import {
  createContext,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { BottomSheetMethods } from "@gorhom/bottom-sheet/lib/typescript/types";
import { StyleSheet } from "react-native";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { SheetIds } from "../utils/sheet-ids";

type Sheet = {
  ref: RefObject<BottomSheetMethods>;
  resolvePromise: (value: any | PromiseLike<any>) => void | null;
  payload?: any;
};

const SheetContext = createContext<{
  sheets: {
    [key: string]: Sheet;
  };
  register: (sheetId: string, ref: RefObject<BottomSheetMethods>) => void;
  open: (sheetId: string, payload?: any) => Promise<any>;
  close: (sheetId: string, payload?: any) => void;
  dismiss: (sheetId: string) => void;
}>({
  sheets: {},
  register: () => {},
  open: () => Promise.resolve(null),
  close: () => {},
  dismiss: () => {},
});

export const useSheet = <T, S>(sheetId: SheetIds) => {
  const { sheets, register, open, close, dismiss } = useContext(SheetContext);

  const registerSheet = useCallback((ref: RefObject<BottomSheetMethods>) => {
    return register(sheetId, ref);
  }, [register, sheetId]);

  const openSheet = useCallback((payload?: T) => {
    return open(sheetId, payload) as Promise<S>;
  }, [open, sheetId]);

  const closeSheet = useCallback((payload?: S) => {
    return close(sheetId, payload);
  }, [close, sheetId]);

  const dismissSheet = useCallback(() => {
    return dismiss(sheetId);
  }, [dismiss, sheetId]);

  return {
    sheet: sheets[sheetId],
    register: registerSheet,
    open: openSheet,
    close: closeSheet,
    dismiss: dismissSheet,
  };
};

function SheetProvider({ children }: { children: ReactNode }) {
  const [sheets, setSheets] = useState<{ [key: string]: Sheet }>({});

  const register = useCallback(
    (sheetId: string, ref: RefObject<BottomSheetMethods>) => {
      setSheets((prev) => {
        if (prev[sheetId]) return prev;
        const temp = { ...prev };
        temp[sheetId] = { ref, resolvePromise: () => null };
        return temp;
      });
    },
    []
  );

  const open = useCallback((sheetId: string, payload?: any) => {
    return new Promise((resolve) => {
      setSheets((prev) => {
        const temp = { ...prev };
        temp[sheetId].payload = { ...payload };
        setTimeout(() => {
          temp[sheetId].ref.current?.expand();
          temp[sheetId].resolvePromise = resolve;
        }, 100);
        return temp;
      });
    });
  }, []);

  const close = useCallback((sheetId: string, payload?: any) => {
    setSheets((prev) => {
      const temp = { ...prev };
      temp[sheetId].ref.current?.close();
      temp[sheetId].resolvePromise?.(payload);
      return temp;
    });
  }, []);

  const dismiss = useCallback((sheetId: string) => {
    setSheets((prev) => {
      const temp = { ...prev };
      temp[sheetId].resolvePromise?.(null);
      temp[sheetId].resolvePromise = () => null;
      return temp;
    });
  }, []);

  const value = useMemo(
    () => ({ sheets, register, open, close, dismiss }),
    [sheets, register, open, close, dismiss]
  );

  return (
    <GestureHandlerRootView style={styles.container}>
      <SheetContext.Provider value={value}>{children}</SheetContext.Provider>
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

export default SheetProvider;
